"use client";

import { useCallback, useState } from "react";
import { useCheckPermissions } from "./useCheckPermissions";
import { useAccountActionPermissions } from "./useAccountActionPermissions";
import { useUserAccountRole } from "./useUserAccountRole";

/** Optional template (or minimal owner info) for synchronous owner check and no menu-open delay. */
export interface UseTemplateActionPermissionsOptions {
  /** Template with hydrated owner (id = customuser id as string). When present, canDelete is true immediately for the owner. Accepts BFF owner union (hydrated or raw). */
  template?: {
    owner?: { id?: string } | { customUserId?: number; accountId?: number };
  };
}

/**
 * Returns what actions a user can perform on a Template.
 * When options.template.owner.id is provided and matches the current user, canDelete is true immediately without waiting for the permissions API (matching Zap behavior). A permissions check may still run when the menu is opened for other actions.
 */
export function useTemplateActionPermissions(
  templateId: string,
  options?: UseTemplateActionPermissionsOptions,
) {
  const { customuserId } = useUserAccountRole();

  // If list data says current user is owner, canDelete is true without waiting for the permissions API.
  const isTemplateOwner =
    customuserId !== undefined &&
    options?.template?.owner &&
    "id" in options.template.owner &&
    String(customuserId) === String(options.template.owner.id);

  /** List owner id is present and is not the current user — cannot change owner from UI regardless of stale permission cache. */
  const listShowsAnotherOwner =
    customuserId !== undefined &&
    options?.template?.owner &&
    "id" in options.template.owner &&
    options.template.owner.id !== undefined &&
    options.template.owner.id !== "" &&
    String(customuserId) !== String(options.template.owner.id);

  // a toggleable state to opt in for fetching permissions
  const [permissionsCheckEnabled, setPermissionsCheckEnabled] = useState(false);

  const { permissions, checkPermissions, isLoading, isFetched } =
    useCheckPermissions({
      assetId: templateId,
      assetType: "template",
      permissionLevels: [
        "can_view",
        "can_edit",
        "can_delete",
        "can_change_owner",
      ],
    });

  const enablePermissionsCheck = useCallback(() => {
    setPermissionsCheckEnabled(true);
    checkPermissions();
  }, [checkPermissions]);

  const { allowTeams: hasTeams } = useAccountActionPermissions();

  const canShareWithOrganization = !!(permissions?.canEdit && hasTeams);
  /** Owner (from list) or FGA edit — open Management share modal for public link without team plan. */
  const canOpenTemplateShareModal =
    isTemplateOwner || !!(permissions?.canEdit ?? false);

  /** True once a permission check the user opted into has finished loading (success or error). */
  const isTemplatePermissionCheckSettled =
    permissionsCheckEnabled && !isLoading && isFetched;

  return {
    canDelete: isTemplateOwner || (permissions?.canDelete ?? false),
    canEditTemplate: isTemplateOwner || !!(permissions?.canEdit ?? false),
    canRename: permissions?.canEdit || false,
    canMove: permissions?.canEdit || false,
    canChangeOwner: listShowsAnotherOwner
      ? false
      : (permissions?.canChangeOwner && hasTeams) || false,
    /** Alias of `canShareWithOrganization` (org/workspace tab). Kept for existing callers. */
    canShare: canShareWithOrganization,
    canShareWithOrganization,
    canOpenTemplateShareModal,
    canDuplicate: true, // anyone with view access can duplicate a template
    canUseTemplate: !!(permissions?.canView || permissions?.canEdit),
    enablePermissionsCheck,
    isLoading,
    isTemplatePermissionCheckSettled,
    hasTeams,
  };
}
